import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MessageCircle, ThumbsDown, ThumbsUp, Meh, X, Send } from 'lucide-react';
import { submitFeedback, type FeedbackRating } from '../api/client';

type RatingOption = {
  value: FeedbackRating;
  icon: React.ReactNode;
  labelKey: string;
};

/** Panel page bottom padding so scroll content clears the fixed FAB (bottom-4 + button height). */
export const FEEDBACK_WIDGET_PAGE_BOTTOM_PADDING = 'pb-14';

const RATINGS: RatingOption[] = [
  { value: 'dislike', icon: <ThumbsDown className="h-5 w-5" />, labelKey: 'feedback.dislike' },
  { value: 'neutral', icon: <Meh className="h-5 w-5" />, labelKey: 'feedback.neutral' },
  { value: 'like', icon: <ThumbsUp className="h-5 w-5" />, labelKey: 'feedback.like' },
];

export default function FeedbackWidget() {
  const { t } = useTranslation();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState<FeedbackRating | null>(null);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Hidden while the App Screens AI chat drawer is open so it doesn't overlap the chat.
  const [chatDrawerOpen, setChatDrawerOpen] = useState(false);

  useEffect(() => {
    const handler = (e: Event) => setChatDrawerOpen(Boolean((e as CustomEvent).detail));
    window.addEventListener('appscreens:chat-open', handler);
    return () => window.removeEventListener('appscreens:chat-open', handler);
  }, []);

  const messageRequired = rating === 'dislike';
  const canSubmit =
    rating != null && (!messageRequired || message.trim().length > 0) && !submitting;

  const reset = () => {
    setRating(null);
    setMessage('');
    setError(null);
    setDone(false);
    setSubmitting(false);
  };

  const handleClose = () => {
    setOpen(false);
    reset();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || rating == null) return;
    setSubmitting(true);
    setError(null);
    try {
      await submitFeedback({
        rating,
        message: message.trim(),
        pagePath: location.pathname,
      });
      setDone(true);
      setTimeout(() => {
        setOpen(false);
        reset();
      }, 1800);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('feedback.error'));
      setSubmitting(false);
    }
  };

  if (chatDrawerOpen) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[90] flex flex-col items-end gap-2">
      {open && (
        <div
          className="w-[min(100vw-2rem,20rem)] rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg"
          role="dialog"
          aria-labelledby="feedback-title"
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 dark:border-gray-700">
            <h2 id="feedback-title" className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {t('feedback.title')}
            </h2>
            <button
              type="button"
              onClick={handleClose}
              className="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
              aria-label={t('common.close')}
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {done ? (
            <p className="px-3 py-6 text-sm text-center text-gray-700 dark:text-gray-200">
              {t('feedback.success')}
            </p>
          ) : (
            <form onSubmit={handleSubmit} className="p-3 space-y-3">
              <p className="text-xs text-gray-500 dark:text-gray-400">{t('feedback.hint')}</p>
              <div className="flex gap-2" role="group" aria-label={t('feedback.ratingLabel')}>
                {RATINGS.map((opt) => {
                  const selected = rating === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setRating(opt.value)}
                      className={`flex-1 flex flex-col items-center gap-1 py-2 px-1 rounded-md border text-xs transition-colors ${
                        selected
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                          : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                      }`}
                      aria-pressed={selected}
                    >
                      {opt.icon}
                      <span>{t(opt.labelKey)}</span>
                    </button>
                  );
                })}
              </div>

              <div>
                <label
                  htmlFor="feedback-message"
                  className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  {t('feedback.messageLabel')}
                  {messageRequired ? (
                    <span className="text-red-500 ml-0.5">*</span>
                  ) : (
                    <span className="text-gray-400 font-normal ml-1">({t('feedback.optional')})</span>
                  )}
                </label>
                <textarea
                  id="feedback-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={3}
                  maxLength={2000}
                  required={messageRequired}
                  placeholder={
                    messageRequired
                      ? t('feedback.messageRequiredPlaceholder')
                      : t('feedback.messagePlaceholder')
                  }
                  className="w-full rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-2.5 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                />
              </div>

              {error && (
                <p className="text-xs text-red-600 dark:text-red-400" role="alert">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={!canSubmit}
                className="w-full inline-flex items-center justify-center gap-1.5 rounded-md bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium py-2 px-3 transition-colors"
              >
                <Send className="h-3.5 w-3.5" />
                {submitting ? t('feedback.sending') : t('feedback.submit')}
              </button>
            </form>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={() => (open ? handleClose() : setOpen(true))}
        className="inline-flex items-center gap-2 rounded-full bg-primary-600 hover:bg-primary-700 text-white shadow-lg px-3.5 py-2.5 text-sm font-medium transition-colors"
        aria-expanded={open}
        aria-label={t('feedback.open')}
      >
        <MessageCircle className="h-4 w-4" />
        <span className="hidden sm:inline">{t('feedback.open')}</span>
      </button>
    </div>
  );
}
